import { useEffect, useState } from "react";
import { IconCurrencyDollar, IconExternalLink, IconFileText } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { Button } from "../../../../components/common/button";
import { Shimmer } from "../../../../components/common/shimmer";
import { purchaseOrdersService } from "../../../api_client";
import { useAuthenticatedBlob } from "../../../../hooks/useAuthenticatedBlob";
import { PayVendorBillDialog } from "../../../payments/presentation/components/pay-vendor-bill-dialog";
import type { VendorBillDto } from "../../../payments/data/types";
import { VendorBillPaymentState } from "../../../payments/data/types";

interface SellerInvoice {
  id: number;
  name: string;
  mimetype: string;
  url: string;
}
interface VendorBill {
  id: number;
  name: string;
  previewUrl: string | null;
  paymentState: string | null;
  amountResidual: number | null;
  amountTotal: number | null;
}
interface InvoicesPerPo {
  purchaseOrderId: number;
  purchaseOrderName: string;
  sellerName: string | null;
  sellerInvoice: SellerInvoice | null;
  vendorBill: VendorBill | null;
}

/**
 * Admin moderation surface: shows the seller-uploaded tax invoice and the
 * auto-generated vendor bill side by side, one block per child PO of the
 * sale order. Read-only — admin actions (approval/takedown) belong to a
 * separate flow.
 */
export function AdminOrderInvoicesCard({ saleOrderId }: { saleOrderId: number }) {
  const [rows, setRows] = useState<InvoicesPerPo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    purchaseOrdersService
      .adminGetSaleOrderInvoices(saleOrderId)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data as unknown as InvoicesPerPo[]);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractErrorMessage(err) ?? "Failed to load invoices");
      });
    return () => {
      cancelled = true;
    };
  }, [saleOrderId, reloadKey]);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <Heading variant="h3" tone="dark">
        Seller invoices
      </Heading>
      <Text variant="bodySm" tone="muted">
        For each seller (purchase order) on this sale, view the auto-generated vendor bill and the seller's manually uploaded tax invoice.
      </Text>

      {error && (
        <Text variant="body" tone="body" className="text-accentError">
          {error}
        </Text>
      )}

      {!rows && !error && (
        <Text variant="bodySm" tone="muted">
          Loading invoices…
        </Text>
      )}

      {rows && rows.length === 0 && (
        <Text variant="bodySm" tone="muted">
          No purchase orders linked to this sale yet.
        </Text>
      )}

      {rows && rows.length > 0 && (
        <div className="flex flex-col gap-4">
          {rows.map((row) => (
            <PurchaseOrderInvoiceBlock key={row.purchaseOrderId} row={row} onPaymentSuccess={() => setReloadKey((k) => k + 1)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PurchaseOrderInvoiceBlock({ row, onPaymentSuccess }: { row: InvoicesPerPo; onPaymentSuccess: () => void }) {
  const [payOpen, setPayOpen] = useState(false);
  const vb = row.vendorBill;
  const isPaid = vb?.paymentState === "paid";

  const billDto: VendorBillDto | null = vb
    ? {
        id: vb.id,
        supplierId: null,
        supplierName: row.sellerName,
        reference: vb.name,
        invoiceDate: null,
        dueDate: null,
        amountTotal: vb.amountTotal ?? 0,
        amountResidual: vb.amountResidual ?? vb.amountTotal ?? 0,
        currency: null,
        paymentState: isPaid ? VendorBillPaymentState.PAID : VendorBillPaymentState.NOT_PAID,
        state: "posted",
      }
    : null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-borderDefault bg-bgLight p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <Text variant="bodySmBold" tone="dark">
            {row.purchaseOrderName}
          </Text>
          {row.sellerName && (
            <Text variant="bodyXs" tone="muted">
              Seller: {row.sellerName}
            </Text>
          )}
        </div>
        {vb && (
          isPaid ? (
            <Chip variant="approved">Paid</Chip>
          ) : (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconCurrencyDollar size={16} />}
              onClick={() => setPayOpen(true)}
            >
              Confirm Payment
            </Button>
          )
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <DocBlock
          title="Auto-generated vendor bill"
          subtitle="Platform accounting record."
          empty="Not generated yet — created at delivery."
          name={row.vendorBill?.name ?? null}
          openHref={row.vendorBill?.previewUrl ?? null}
          openTarget="_blank"
        />
        <SellerInvoiceBlock invoice={row.sellerInvoice} />
      </div>

      {billDto && (
        <PayVendorBillDialog
          bill={billDto}
          open={payOpen}
          onClose={() => setPayOpen(false)}
          onSuccess={onPaymentSuccess}
        />
      )}
    </div>
  );
}

function SellerInvoiceBlock({ invoice }: { invoice: SellerInvoice | null }) {
  const { src, error } = useAuthenticatedBlob(invoice?.url);

  if (!invoice) {
    return (
      <DocBlock
        title="Seller tax invoice"
        subtitle="Seller-uploaded regulator-compliant document."
        empty="Not uploaded yet."
        name={null}
        openHref={null}
      />
    );
  }

  const isImage = invoice.mimetype.startsWith("image/");

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-borderDefault bg-bgWhite p-3">
      <Text variant="bodySmBold" tone="dark">
        Seller tax invoice
      </Text>
      <Text variant="bodyXs" tone="muted">
        Seller-uploaded regulator-compliant document.
      </Text>

      {error ? (
        <div className="flex h-24 items-center justify-center rounded-xl bg-bgMuted">
          <Text variant="bodyXs" tone="muted">
            Couldn't load {invoice.name}
          </Text>
        </div>
      ) : !src ? (
        <Shimmer className="h-24 w-full rounded-xl" />
      ) : isImage ? (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="block h-32 overflow-hidden rounded-xl border border-borderDefault bg-bgLight"
        >
          <img src={src} alt={invoice.name} className="h-full w-full object-contain" />
        </a>
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <IconFileText size={18} className="shrink-0 text-textMuted" />
            <Text variant="bodySmBold" tone="dark" className="truncate">
              {invoice.name}
            </Text>
          </div>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-textBody hover:text-textDark"
          >
            <IconExternalLink size={16} />
            <Text variant="bodySm" tone="inherit">
              Open
            </Text>
          </a>
        </div>
      )}
    </div>
  );
}

function DocBlock({
  title,
  subtitle,
  empty,
  name,
  openHref,
  openTarget = "_blank",
}: {
  title: string;
  subtitle: string;
  empty: string;
  name: string | null;
  openHref: string | null;
  openTarget?: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-borderDefault bg-bgWhite p-3">
      <Text variant="bodySmBold" tone="dark">
        {title}
      </Text>
      <Text variant="bodyXs" tone="muted">
        {subtitle}
      </Text>
      {name && openHref ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <IconFileText size={18} className="shrink-0 text-textMuted" />
            <Text variant="bodySmBold" tone="dark" className="truncate">
              {name}
            </Text>
          </div>
          <a
            href={openHref}
            target={openTarget}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-textBody hover:text-textDark"
          >
            <IconExternalLink size={16} />
            <Text variant="bodySm" tone="inherit">
              Open PDF
            </Text>
          </a>
        </div>
      ) : (
        <Text variant="bodySm" tone="muted">
          {empty}
        </Text>
      )}
    </div>
  );
}

function extractErrorMessage(err: unknown): string | null {
  const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(", ") : msg ?? null;
}
